import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTRPC } from '@barev/web';
import { useEffect, useState } from 'react';
import type { Me } from '../App.tsx';
import { useT } from '../i18n.tsx';
import { Avatar, I, Skeleton } from '../components/kit.tsx';
import { ProviderForm } from './ProviderCard.tsx';

type Status = 'active' | 'blocked' | 'all';
const ROLES = ['CLEANER', 'HANDYMAN', 'DRIVER', 'TEACHER', 'CAREER_CONSULTANT', 'DOCS_AGENT'] as const;

export function Providers({ me }: { me: Me }) {
  const { t, shared, dateTime } = useT();
  const trpc = useTRPC();
  const [status, setStatus] = useState<Status>('active');
  const [role, setRole] = useState<(typeof ROLES)[number] | undefined>();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);
  const list = useQuery({ ...trpc.bo.providers.list.queryOptions({ status, role, search: q || undefined }), placeholderData: keepPreviousData });
  const roles = ROLES.filter((r) => role === r || (list.data ?? []).some((p) => p.role === r));

  return (
    <>
      <div className="bo-head">
        <h1 className="bo-title">{t('prv.title')}</h1>
        <button type="button" className="bo-btn bo-btn--accent" onClick={() => setCreating(true)}>
          <I name="plus" size={16} />
          {t('prv.add')}
        </button>
      </div>

      <div className="bo-toolbar">
        <label className="bo-search">
          <I name="search" size={18} />
          <input className="bo-input" type="search" value={search} placeholder={t('prv.search')} aria-label={t('prv.search')} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <div className="bo-pills" role="group">
          {(['active', 'blocked', 'all'] as const).map((s) => (
            <button key={s} type="button" className="bo-pill" aria-pressed={status === s} onClick={() => setStatus(s)}>
              {t(`prv.status.${s}`)}
            </button>
          ))}
        </div>
        {roles.length > 1 && (
          <div className="bo-pills" role="group">
            {roles.map((r) => (
              <button key={r} type="button" className="bo-pill" aria-pressed={role === r} onClick={() => setRole(role === r ? undefined : r)}>
                {shared(`roles.${r}`)}
              </button>
            ))}
          </div>
        )}
      </div>

      {!list.data ? (
        <div className="bo-col">
          <Skeleton h={72} />
          <Skeleton h={72} />
        </div>
      ) : list.data.length === 0 ? (
        <div className="bo-panel bo-empty">{t('prv.empty')}</div>
      ) : (
        <div className="bo-panel" style={{ padding: 8 }}>
          <table className="bo-table bo-ptable">
            <thead>
              <tr>
                <th>{t('prv.col.name')}</th>
                <th className="bo-r">{t('prv.col.rating')}</th>
                <th className="bo-r">{t('prv.col.done')}</th>
                <th className="bo-r">{t('prv.col.refused')}</th>
                <th>{t('prv.col.next')}</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((p) => (
                <tr key={p.id} onClick={() => (location.hash = `#/providers/${p.id}`)} data-off={!p.active || undefined}>
                  <td>
                    <a href={`#/providers/${p.id}`} className="bo-who" onClick={(e) => e.stopPropagation()}>
                      <span className="bo-pavatar" data-shift={p.onShift || undefined}>
                        <Avatar name={p.name} id={p.id} size={38} />
                      </span>
                      <span style={{ minWidth: 0 }}>
                        <span className="bo-who__name">{p.name}</span>
                        <span className="bo-who__meta">
                          {[shared(`roles.${p.role}`), p.company?.name, p.languages.join(' ').toUpperCase()].filter(Boolean).join(' · ')}
                        </span>
                        <span className="bo-tags" style={{ marginTop: 4 }}>
                          {!p.active && <span className="bo-chip bo-chip--danger">{t('prv.blocked')}</span>}
                          {p.active && p.onShift && <span className="bo-chip bo-chip--ok">{t('prv.onShift')}</span>}
                          {!p.linked && <span className="bo-chip bo-chip--warn">{t('prv.notLinked')}</span>}
                          {p.docs === 0 && p.active && <span className="bo-chip">{t('prv.noDocs')}</span>}
                        </span>
                      </span>
                    </a>
                  </td>
                  <td className="bo-r">
                    {p.rating != null ? (
                      <>
                        <b className="bo-num">★ {p.rating}</b>
                        <span className="bo-faint bo-small" style={{ display: 'block' }}>
                          {t('prv.ratings', { n: p.ratingCount })}
                          {p.low30 > 0 && <span style={{ color: 'var(--bc-danger)' }}> · {t('prv.low', { n: p.low30 })}</span>}
                        </span>
                      </>
                    ) : (
                      <span className="bo-faint">—</span>
                    )}
                  </td>
                  <td className="bo-r bo-num">{p.done30}</td>
                  <td className="bo-r bo-num" style={{ color: p.refused30 >= 3 ? 'var(--bc-danger)' : undefined }}>
                    {p.refused30}
                  </td>
                  <td>
                    {p.next ? (
                      <a href={`#/orders/${p.next.id}`} onClick={(e) => e.stopPropagation()} className="bo-small">
                        №{p.next.number} · {dateTime(p.next.scheduledAt)}
                      </a>
                    ) : (
                      <span className="bo-faint">—</span>
                    )}
                    {p.today > 0 && (
                      <span className="bo-faint bo-small" style={{ display: 'block' }}>
                        {t('prv.today', { n: p.today })}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="bo-hint" style={{ padding: '10px 12px 4px' }}>
            {t('prv.period30')}
          </p>
        </div>
      )}
      {creating && <ProviderForm me={me} onClose={() => setCreating(false)} />}
    </>
  );
}
